<script setup>
import api from "@/api/http";
import httpApiHos from "@/api/httpApiHos";
import { showErrorAlert } from "@/utils/sweetAlert";
import { ref, reactive, watch } from "vue";
import AppTitle from "./layouts/AppTitle.vue";

import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const currentUser = computed(() => store.getters.currentUser);

const values = reactive({
  cid: "",
  hn: "",
  an: "",
  name: "",
  birthday: "",
  address: "",
  prediag: "", // อาการสำคัญ
  ward_name: "",

  temperature: "",
  map: "",
  heart_rate: "",
  respiratory_rate: "",
  oxygenation_a: "",
  oxygenation_b: "",
  arterial_ph: "",
  serum_sodium: "",
  serum_potassium: "",
  serum_creatinine: "",
  hematocrit: "",
  wbc: "",
  gcs: "",
  serum_hco3: "",
  age: "",
  chronic: "",
  chronic_pts: 0,
  recorder_name: currentUser.value.name || "",
});

const searchAN = async () => {
  if (!values.an) {
    alert("กรุณากรอก AN ก่อนค้นหา");
    return;
  }

  try {
    const res = await httpApiHos.get(`/ipt/${values.an}`);

    const data = await res.data;

    // Autofill form fields
    values.cid = data.cid || "";
    values.hn = data.hn || "";
    values.name = `${data.pname || ""} ${data.fname || ""} ${
      data.lname || ""
    }`.trim();
    values.address = data.fulladdressname || "";
    values.prediag = data.prediag || "";
    values.ward_name = data.name || ""; // ward name from API
    values.birthday = data.birthday;
    values.age = calculateAge(data.birthday); // auto calculate age if birthday exists
  } catch (error) {
    alert("ค้นหาไม่สำเร็จ: " + error.message);
  }
};

// Helper function: calculate age from birthday
function calculateAge(dateStr) {
  if (!dateStr) return "";
  const birth = new Date(dateStr);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

const aps_score = ref(0);
const age_points = ref(0);
const total_score = ref(0);
const mortality = ref(0);

const calculatePoints = (variable, value) => {
  const val = parseFloat(value);
  if (isNaN(val)) return 0;

  // Chronic health points

  switch (values.chronic) {
    case "elective":
      values.chronic_pts = 2;
      break;
    case "emergency":
    case "nonoperative":
      values.chronic_pts = 5;
      break;
    default:
      values.chronic_pts = 0;
  }

  switch (variable) {
    case "temperature":
      if (val >= 41) return 4;
      if (val >= 39) return 3;
      if (val >= 38.5) return 1;
      if (val >= 36 && val <= 38.4) return 0;
      if (val >= 34 && val < 36) return 1;
      if (val >= 32 && val < 34) return 2;
      if (val >= 30 && val < 32) return 3;
      if (val < 30) return 4;
      break;
    case "map":
      if (val >= 160) return 4;
      if (val >= 130) return 3;
      if (val >= 110) return 2;
      if (val >= 70 && val <= 109) return 0;
      if (val >= 50 && val < 70) return 2;
      if (val < 50) return 4;
      break;
    case "heart_rate":
      if (val >= 180) return 4;
      if (val >= 140) return 3;
      if (val >= 110) return 2;
      if (val >= 70 && val <= 109) return 0;
      if (val >= 55 && val < 70) return 2;
      if (val >= 40 && val < 55) return 3;
      if (val < 40) return 4;
      break;
    case "respiratory_rate":
      if (val >= 50) return 4;
      if (val >= 35) return 3;
      if (val >= 25) return 1;
      if (val >= 12 && val <= 24) return 0;
      if (val >= 10 && val < 12) return 1;
      if (val >= 6 && val < 10) return 2;
      if (val < 6) return 4;
      break;
    case "oxygenation_a":
      if (val < 200) return 0;
      if (val <= 349) return 2;
      if (val <= 499) return 3;
      if (val >= 500) return 4;
      break;
    case "oxygenation_b":
      if (val > 70) return 0;
      if (val >= 61 && val <= 70) return 1;
      if (val >= 55 && val <= 60) return 2;
      if (val < 55) return 4;
      break;
    case "arterial_ph":
      if (val >= 7.7) return 4;
      if (val >= 7.6 && val < 7.7) return 3;
      if (val >= 7.5 && val < 7.6) return 1;
      if (val >= 7.33 && val <= 7.49) return 0;
      if (val >= 7.25 && val < 7.33) return 2;
      if (val >= 7.15 && val < 7.25) return 3;
      if (val < 7.15) return 4;
      break;
    case "serum_sodium":
      if (val >= 180) return 4;
      if (val >= 160) return 3;
      if (val >= 155) return 2;
      if (val >= 150) return 1;
      if (val >= 130 && val <= 149) return 0;
      if (val >= 120 && val < 130) return 2;
      if (val >= 111 && val < 120) return 3;
      if (val < 111) return 4;
      break;
    case "serum_potassium":
      if (val >= 7) return 4;
      if (val >= 6) return 3;
      if (val >= 5.5) return 1;
      if (val >= 3.5 && val <= 5.3) return 0;
      if (val >= 3.0 && val < 3.5) return 1;
      if (val >= 2.5 && val < 3.0) return 2;
      if (val < 2.5) return 4;
      break;
    case "serum_creatinine":
      if (val >= 3.5) return 4;
      if (val >= 2) return 3;
      if (val >= 1.5) return 2;
      if (val >= 0.6 && val <= 1.4) return 0;
      if (val < 0.6) return 2;
      break;
    case "hematocrit":
      if (val >= 60) return 4;
      if (val >= 50) return 2;
      if (val >= 46 && val < 50) return 1;
      if (val >= 30 && val <= 45.9) return 0;
      if (val >= 20 && val < 30) return 2;
      if (val < 20) return 4;
      break;
    case "wbc":
      if (val >= 40) return 4;
      if (val >= 20) return 2;
      if (val >= 15) return 1;
      if (val >= 3 && val <= 14.9) return 0;
      if (val >= 1 && val < 3) return 2;
      if (val < 1) return 4;
      break;
    case "gcs":
      return 15 - parseInt(value);
    case "serum_hco3":
      if (val >= 52) return 4;
      if (val >= 41) return 3;
      if (val >= 32) return 1;
      if (val >= 22 && val <= 31.9) return 0;
      if (val >= 18 && val < 22) return 2;
      if (val >= 15 && val < 18) return 3;
      if (val < 15) return 4;
      break;
    default:
      return 0;
  }
  return 0;
};

watch(
  values,
  () => {
    let aps = 0;
    Object.keys(values).forEach((key) => {
      if (key !== "age" && values[key] !== "") {
        aps += calculatePoints(key, values[key]);
      }
    });
    aps_score.value = aps;

    const age = parseInt(values.age);
    let agePts = 0;

    if (!isNaN(age)) {
      if (age > 70) agePts = 6;
      else if (age >= 65) agePts = 4;
      else if (age >= 55) agePts = 3;
      else if (age >= 45) agePts = 2;
      else if (age >= 40) agePts = 1;
      else agePts = 0;
    }
    age_points.value = agePts;

    const total = aps + agePts + values.chronic_pts;
    total_score.value = total;

    if (total <= 4) mortality.value = 4;
    else if (total <= 9) mortality.value = 8;
    else if (total <= 14) mortality.value = 15;
    else if (total <= 19) mortality.value = 25;
    else if (total <= 24) mortality.value = 40;
    else if (total <= 29) mortality.value = 55;
    else if (total <= 34) mortality.value = 75;
    else mortality.value = 85;
  },
  { deep: true }
);

const saveData = async () => {
  const payload = {
    ...values,
    aps: aps_score.value,
    age_points: age_points.value,
    chronic_points: values.chronic_pts,
    total_score: total_score.value,
    mortality: mortality.value,
  };

  try {
    await api.post("apache", payload);
  } catch (error) {
    showErrorAlert("เกิดข้อผิดพลาด", "ไม่สามารถบันทึกข้อมูลได้");
  }
  alert("Saved!");
};
</script>

<template>
  <div>
    <AppTitle name="APACHE II Screening" />

    <div class="col-md-12">
      <div class="tile">
        <!-- Loading Overlay -->
        <LoadingOverlay v-if="isLoading" />

        <div class="tile-title-w-btn">
          <h3 class="title">APACHE II Score</h3>
        </div>

        <div class="tile-body">
          <!-- <h2 class="mb-0 text-primary fw-bold">APACHE II Score</h2> -->
          <!-- <p class="text-muted">Acute Physiology and Chronic Health Evaluation II</p> -->

          <!-- Patient Information -->
          <h4 class="border-bottom pb-2 mt-4 text-primary">
            Patient Information
          </h4>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold">AN</label>
            <div class="col-md-7">
              <input
                type="text"
                v-model="values.an"
                class="form-control"
                placeholder="Enter AN"
              />
            </div>
            <div class="col-md-2">
              <button class="btn btn-primary w-100" @click="searchAN">
                Search
              </button>
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold">CID</label>
            <div class="col-md-9">
              <input
                type="text"
                readonly
                v-model="values.cid"
                class="form-control"
                placeholder="Enter CID"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold">HN</label>
            <div class="col-md-9">
              <input
                type="text"
                readonly
                v-model="values.hn"
                class="form-control"
                placeholder="Enter HN"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold">Name</label>
            <div class="col-md-9">
              <input
                type="text"
                readonly
                v-model="values.name"
                class="form-control"
                placeholder="Enter patient name"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold">Birthday</label>
            <div class="col-md-9">
              <input
                type="text"
                readonly
                v-model="values.birthday"
                class="form-control"
                placeholder="Enter Birthday"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold">Address</label>
            <div class="col-md-9">
              <input
                type="text"
                readonly
                v-model="values.address"
                class="form-control"
                placeholder="Enter address"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold"
              >อาการสำคัญ (Prediag)</label
            >
            <div class="col-md-9">
              <input
                type="text"
                readonly
                v-model="values.prediag"
                class="form-control"
                placeholder="กรอกอาการสำคัญ"
              />
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold">Ward Name</label>
            <div class="col-md-9">
              <input
                type="text"
                readonly
                v-model="values.ward_name"
                class="form-control"
                placeholder="Enter ward"
              />
            </div>
          </div>

          <!-- Vital Signs -->
          <h4 class="border-bottom pb-2 mt-4 text-primary">Vital Signs</h4>

          <!-- Temperature -->
          <div class="row mb-3 align-items-center">
            <label class="col-md-3 col-form-label fw-semibold"
              >Temperature</label
            >
            <div class="col-md-9">
              <div class="input-group">
                <span class="input-group-text bg-light text-muted"
                  >Norm: 36-38.4</span
                >
                <input
                  type="number"
                  v-model="values.temperature"
                  class="form-control"
                  placeholder="Enter °C"
                />
                <span class="input-group-text bg-light">°C</span>
              </div>
            </div>
          </div>

          <!-- MAP -->
          <div class="row mb-3 align-items-center">
            <label class="col-md-3 col-form-label fw-semibold"
              >Mean Arterial Pressure</label
            >
            <div class="col-md-9">
              <div class="input-group">
                <span class="input-group-text bg-light text-muted"
                  >Norm: 70 – 109</span
                >
                <input
                  type="number"
                  v-model="values.map"
                  class="form-control"
                  placeholder="mmHg"
                />
                <span class="input-group-text bg-light">mmHg</span>
              </div>
            </div>
          </div>

          <!-- Heart Rate -->
          <div class="row mb-3 align-items-center">
            <label class="col-md-3 col-form-label fw-semibold"
              >Heart Rate</label
            >
            <div class="col-md-9">
              <div class="input-group">
                <span class="input-group-text bg-light text-muted"
                  >Norm: 70 – 109</span
                >
                <input
                  type="number"
                  v-model="values.heart_rate"
                  class="form-control"
                  placeholder="bpm"
                />
                <span class="input-group-text bg-light">bpm</span>
              </div>
            </div>
          </div>

          <!-- Respiratory Rate -->
          <div class="row mb-3 align-items-center">
            <label class="col-md-3 col-form-label fw-semibold"
              >Respiratory Rate</label
            >
            <div class="col-md-9">
              <div class="input-group">
                <span class="input-group-text bg-light text-muted"
                  >Norm: 12 – 24</span
                >
                <input
                  type="number"
                  v-model="values.respiratory_rate"
                  class="form-control"
                  placeholder="breaths/min"
                />
                <span class="input-group-text bg-light">/min</span>
              </div>
            </div>
          </div>

          <!-- Oxygenation A-aDO2 -->
          <div class="row mb-3 align-items-center">
            <label class="col-md-3 col-form-label fw-semibold"
              >Oxygenation (A-aDO₂)</label
            >
            <div class="col-md-9">
              <div class="input-group">
                <span class="input-group-text bg-light text-muted"
                  >Norm &lt; 200</span
                >
                <input
                  type="number"
                  v-model="values.oxygenation_a"
                  class="form-control"
                  placeholder="aDO₂"
                />
                <span class="input-group-text bg-light">mmHg</span>
              </div>
            </div>
          </div>

          <!-- Oxygenation PaO2 -->
          <div class="row mb-3 align-items-center">
            <label class="col-md-3 col-form-label fw-semibold"
              >Oxygenation (B-PaO₂)</label
            >
            <div class="col-md-9">
              <div class="input-group">
                <span class="input-group-text bg-light text-muted"
                  >Norm &gt; 70</span
                >
                <input
                  type="number"
                  v-model="values.oxygenation_b"
                  class="form-control"
                  placeholder="PaO₂"
                />
                <span class="input-group-text bg-light">mmHg</span>
              </div>
            </div>
          </div>

          <!-- GCS -->
          <div class="row mb-3 align-items-center">
            <label class="col-md-3 col-form-label fw-semibold"
              >Glasgow Coma Score</label
            >
            <div class="col-md-9">
              <input
                type="number"
                v-model="values.gcs"
                class="form-control"
                placeholder="15 = Normal"
              />
            </div>
          </div>

          <!-- Laboratory Values -->
          <h4 class="border-bottom pb-2 mt-4 text-primary">
            Laboratory Values
          </h4>

          <!-- <div class="row">
          <div class="col-md-6">
            <div
              v-for="(label, key) in {
                arterial_ph: 'Arterial pH',
                serum_sodium: 'Serum Sodium (mmol/L)',
                serum_potassium: 'Serum Potassium (mmol/L)',
                serum_creatinine: 'Serum Creatinine (mg/100 ml)',
              }"
              :key="key"
              class="mb-3"
            >
              <label class="form-label fw-semibold">{{ label }}</label>
              <input type="number" v-model="values[key]" class="form-control" />
            </div>
          </div>

          <div class="col-md-6">
            <div
              v-for="(label, key) in {
                hematocrit: 'Hematocrit (%)',
                wbc: 'White Blood Count (×1000)',
                serum_hco3: 'Serum HCO₃ (mmol/L)',
              }"
              :key="key"
              class="mb-3"
            >
              <label class="form-label fw-semibold">{{ label }}</label>
              <input type="number" v-model="values[key]" class="form-control" />
            </div>
          </div>
        </div> -->

          <div class="row">
            <div class="col-md-6">
              <div
                v-for="(label, key) in {
                  arterial_ph: 'Arterial pH',
                  serum_sodium: 'Serum Sodium (mmol/L)',
                  serum_potassium: 'Serum Potassium (mmol/L)',
                  serum_creatinine: 'Serum Creatinine (mg/100 ml)',
                }"
                :key="key"
                class="mb-3"
              >
                <label class="form-label fw-semibold">{{ label }}</label>
                <input
                  type="number"
                  v-model="values[key]"
                  class="form-control"
                  :placeholder="
                    {
                      arterial_ph: 'Norm: 7.33–7.49',
                      serum_sodium: 'Norm: 130–149',
                      serum_potassium: 'Norm: 3.5–5.3',
                      serum_creatinine: 'Norm: 0.6–1.4', // No norm provided
                    }[key]
                  "
                />
              </div>
            </div>

            <div class="col-md-6">
              <div
                v-for="(label, key) in {
                  hematocrit: 'Hematocrit (%)',
                  wbc: 'White Blood Count (×1000)',
                  serum_hco3: 'Serum HCO₃ (mmol/L)',
                }"
                :key="key"
                class="mb-3"
              >
                <label class="form-label fw-semibold">{{ label }}</label>
                <input
                  type="number"
                  v-model="values[key]"
                  class="form-control"
                  :placeholder="
                    {
                      hematocrit: 'Norm: 30–45.9',
                      wbc: 'Norm: 3–14.9',
                      serum_hco3: 'Norm: 22–31.9',
                    }[key]
                  "
                />
              </div>
            </div>
          </div>

          <h4 class="border-bottom pb-2 mt-4 text-primary">
            Chronic Health Status
          </h4>

          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold"
              >Chronic Health</label
            >
            <div class="col-md-9">
              <select v-model="values.chronic" class="form-select">
                <option value="">Choose...</option>
                <option value="elective">ผ่าตัด Elective (2 คะแนน)</option>
                <option value="emergency">
                  ผ่าตัดฉุกเฉิน Emergency (5 คะแนน)
                </option>
                <option value="nonoperative">
                  ไม่ได้ผ่าตัด / อวัยวะล้มเหลวเรื้อรัง (5 คะแนน)
                </option>
              </select>
            </div>
          </div>

          <!-- Chronic Health Explanation -->
          <div class="alert alert-secondary mt-2" style="font-size: 0.9rem">
            <strong>ความหมายของ Chronic Health Status:</strong><br />
            ผู้ป่วย admission โดยมี ประวัติอวัยวะทำงานล้มเหลว (severe organ
            system insufficiency) เช่น
            <ul class="mt-2 mb-1">
              <!-- <li><strong>ตับ:</strong> โรคตับแข็ง (Cirrhosis), portal hypertension, มีประวัติ GI bleeding</li> -->
              <!-- <li><strong>หัวใจ:</strong> ภาวะหัวใจล้มเหลวระดับ Class IV ตามเกณฑ์ NYHA</li> -->
              <li>ตับ เป็นโรคตับแข็งหรือ potal hypertension, GI bleeding</li>
              <li>
                หัวใจ หัวใจวาย class IV (แบ่งตาม New York Heart Association)
              </li>
              <li>
                มีปัญหาเกี่ยวกับภูมิต้านทาน (Immunocompromised) เช่น
                ได้รับยาเคมีบำบัด ได้รับรังสีรักษา ได้รับการรักษาด้วยยากลุ่ม
                steroids ในขนาดสูงๆทั้ง long term และ recent , leukemia,
                lymphoma และ AIDs
              </li>
              <li>
                ระบบหายใจ เป็น COPD, hypoxia, hypercapnia, secondary
                polycythemia, severe pulmonary hypertension (> 40 mmHg)
                และผู้ที่ไม่สามารถหย่า (wean) เครื่องช่วยหายใจได้
              </li>
              <li>ไตวายเรื้อรัง หรือผู้ที่รับการฟอกไต (hemodialysis)</li>
            </ul>
          </div>

          <!-- Age -->
          <h4 class="border-bottom pb-2 mt-4 text-primary">Age</h4>
          <div class="row mb-3">
            <label class="col-md-3 col-form-label fw-semibold"
              >Age (years)</label
            >
            <div class="col-md-9">
              <input
                type="number"
                v-model="values.age"
                class="form-control"
                placeholder="Enter age"
              />
            </div>
          </div>

          <!-- Results -->
          <h4 class="border-bottom pb-2 mt-4 text-primary">Results</h4>

          <div class="row text-center">
            <div class="col-md-3 mb-3">
              <div class="p-3 bg-light rounded shadow-sm">
                <h6 class="text-muted mb-1">APS</h6>
                <h3 class="text-primary fw-bold">{{ aps_score }}</h3>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="p-3 bg-light rounded shadow-sm">
                <h6 class="text-muted mb-1">Age Points</h6>
                <h3 class="text-primary fw-bold">{{ age_points }}</h3>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="p-3 bg-light rounded shadow-sm">
                <h6 class="text-muted mb-1">Chronic Health Points</h6>
                <h3 class="text-primary fw-bold">
                  {{
                    values.chronic === "elective"
                      ? 2
                      : ["nonoperative", "emergency"].includes(values.chronic)
                      ? 5
                      : 0
                  }}
                </h3>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="p-3 bg-light rounded shadow-sm">
                <h6 class="text-muted mb-1">Total APACHE II Score</h6>
                <h3 class="text-primary fw-bold">{{ total_score }}</h3>
              </div>
            </div>
          </div>

          <div class="alert alert-secondary text-center mt-4">
            <h5 class="mb-1">Predicted Hospital Mortality</h5>
            <h2
              :class="
                mortality >= 40
                  ? 'text-danger'
                  : mortality >= 25
                  ? 'text-warning'
                  : 'text-success'
              "
            >
              {{ mortality }}%
            </h2>
          </div>

          <button class="btn btn-lg btn-success w-100 mt-4" @click="saveData">
            SAVE APACHE II
          </button>
        </div>

        <div class="tile-footer">
          <small v-if="currentUser">
            [ชื่อผู้ป่วย: {{ values.name }} | ผู้บันทึกข้อมูล:
            {{ currentUser.name }}]
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
body {
  background-color: #f8f9fa;
}
</style>
